import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { IDrawing } from '../drawing-queue/drawing.interface';
import { ApiService } from '../shared/api.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-database-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './database-admin.component.html',
  styleUrls: ['./database-admin.component.scss']
})
export class DatabaseAdminComponent implements OnInit, OnDestroy {
  public allDrawings: IDrawing[] = [];
  public filteredDrawings: IDrawing[] = [];
  public loading: boolean = false;
  public searchTerm: string = '';
  public statusFilter: string = 'all';
  public editingDrawing: IDrawing | null = null;
  public showDeleteConfirm: IDrawing | null = null;
  public selectedDrawings: Set<string> = new Set(); // Store PK-SK combinations
  public bulkEditMode: boolean = false;
  public bulkStatus: 'pending' | 'approved' | 'denied' | '' = '';
  public bulkApprovalTimestamp: string = '';
  public bulkDenialTimestamp: string = '';
  public bulkClearedTimestamp: string = '';
  public showBulkConfirm: boolean = false;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.scanDatabase();
  }

  ngOnDestroy(): void {
    // Clean up if needed
  }

  async scanDatabase(): Promise<void> {
    this.loading = true;
    try {
      // Use the API service for scanning all drawings
      this.apiService.scanAllDrawings().subscribe({
        next: (data) => {
          this.allDrawings = data.drawings || [];
          console.log('Loaded drawings count:', this.allDrawings.length);
          console.log('First few drawings:', this.allDrawings.slice(0, 3).map(d => ({ PK: d.PK, SK: d.SK, type: typeof d.SK })));
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Failed to scan database:', error);
          this.loading = false;
        }
      });
    } catch (error) {
      console.error('Error scanning database:', error);
      this.loading = false;
    }
  }

  applyFilters(): void {
    this.filteredDrawings = this.allDrawings.filter(drawing => {
      const matchesSearch = this.searchTerm === '' || 
        drawing.PK.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        String(drawing.SK).includes(this.searchTerm);
      
      const matchesStatus = this.statusFilter === 'all' || 
        drawing.ApprovalStatus === this.statusFilter;

      return matchesSearch && matchesStatus;
    });
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  editDrawing(drawing: IDrawing): void {
    this.editingDrawing = { ...drawing }; // Create a copy for editing
  }

  cancelEdit(): void {
    this.editingDrawing = null;
  }

  async saveDrawing(): Promise<void> {
    if (!this.editingDrawing) return;

    this.loading = true;
    try {
      await this.apiService.updateDrawing(this.editingDrawing).toPromise();

      // Update the local array
      const index = this.allDrawings.findIndex(d => 
        d.PK === this.editingDrawing!.PK && d.SK === this.editingDrawing!.SK
      );
      if (index !== -1) {
        this.allDrawings[index] = { ...this.editingDrawing };
        this.applyFilters();
      }
      this.editingDrawing = null;
    } catch (error) {
      console.error('Error updating drawing:', error);
    } finally {
      this.loading = false;
    }
  }

  confirmDelete(drawing: IDrawing): void {
    this.showDeleteConfirm = drawing;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
  }

  async deleteDrawing(): Promise<void> {
    if (!this.showDeleteConfirm) return;

    this.loading = true;
    try {
      await this.apiService.deleteDrawing(this.showDeleteConfirm).toPromise();

      // Remove from local array
      this.allDrawings = this.allDrawings.filter(d => 
        !(d.PK === this.showDeleteConfirm!.PK && d.SK === this.showDeleteConfirm!.SK)
      );
      this.applyFilters();
      this.showDeleteConfirm = null;
    } catch (error) {
      console.error('Error deleting drawing:', error);
    } finally {
      this.loading = false;
    }
  }

  formatDate(timestamp: string | number): string {
    const numTimestamp = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
    return new Date(numTimestamp).toLocaleString();
  }

  formatApprovalTimestamp(timestamp?: number): string {
    return timestamp ? new Date(timestamp).toLocaleString() : 'N/A';
  }

  toggleBulkEditMode(): void {
    this.bulkEditMode = !this.bulkEditMode;
    if (!this.bulkEditMode) {
      this.selectedDrawings.clear();
    }
  }

  toggleDrawingSelection(drawing: IDrawing): void {
    const key = `${drawing.PK}|${drawing.SK}`;
    if (this.selectedDrawings.has(key)) {
      this.selectedDrawings.delete(key);
    } else {
      this.selectedDrawings.add(key);
    }
  }

  isDrawingSelected(drawing: IDrawing): boolean {
    const key = `${drawing.PK}|${drawing.SK}`;
    return this.selectedDrawings.has(key);
  }

  selectAllVisible(): void {
    this.filteredDrawings.forEach(drawing => {
      const key = `${drawing.PK}|${drawing.SK}`;
      this.selectedDrawings.add(key);
    });
  }

  deselectAll(): void {
    this.selectedDrawings.clear();
  }

  initiateBulkEdit(): void {
    if (this.selectedDrawings.size === 0) return;
    this.showBulkConfirm = true;
  }

  cancelBulkEdit(): void {
    this.showBulkConfirm = false;
    this.bulkStatus = '';
    this.bulkApprovalTimestamp = '';
    this.bulkDenialTimestamp = '';
    this.bulkClearedTimestamp = '';
  }

  async executeBulkEdit(): Promise<void> {
    console.log('executeBulkEdit called');
    console.log('Selected drawings:', this.selectedDrawings.size);
    console.log('Bulk status:', this.bulkStatus);
    console.log('Bulk approval timestamp:', this.bulkApprovalTimestamp);
    console.log('Bulk denial timestamp:', this.bulkDenialTimestamp);
    console.log('Bulk cleared timestamp:', this.bulkClearedTimestamp);

    if (this.selectedDrawings.size === 0) {
      console.log('No drawings selected, returning early');
      return;
    }

    // Check if at least one field has a value
    const hasChanges = this.bulkStatus !== '' || 
                      this.bulkApprovalTimestamp !== '' || 
                      this.bulkDenialTimestamp !== '' || 
                      this.bulkClearedTimestamp !== '';

    if (!hasChanges) {
      console.log('No changes to apply, returning early');
      alert('Please provide at least one field to update.');
      return;
    }

    this.loading = true;
    const promises: Promise<any>[] = [];
    const selectedKeys = Array.from(this.selectedDrawings);

    console.log('Processing', selectedKeys.length, 'drawings');

    for (const key of selectedKeys) {
      console.log('Processing key:', key);
      const pipeIndex = key.indexOf('|');
      
      if (pipeIndex === -1) {
        console.warn('Invalid key format (no pipe found):', key);
        continue;
      }
      
      const PK = key.substring(0, pipeIndex);
      const skString = key.substring(pipeIndex + 1);
      const SK = parseInt(skString); // Convert back to number since SK is stored as number
      
      console.log('Parsed PK:', PK, 'SK:', SK, '(converted from string:', skString, ')');
      
      // Find drawing with PK and numeric SK
      const drawing = this.allDrawings.find(d => d.PK === PK && d.SK === SK);
      
      if (!drawing) {
        console.warn('Drawing not found for PK:', PK, 'SK:', SK);
        console.log('Available drawings with matching PK:', 
          this.allDrawings.filter(d => d.PK === PK).map(d => ({ PK: d.PK, SK: d.SK, SKType: typeof d.SK })));
        continue;
      }
      
      console.log('Found drawing:', drawing.PK, drawing.SK);
      
      console.log('Found drawing:', drawing.PK, drawing.SK);
      
      if (drawing) {
        const updatedDrawing = { ...drawing };

        // Only update status if provided
        if (this.bulkStatus !== '') {
          updatedDrawing.ApprovalStatus = this.bulkStatus;
          console.log(`Updating ${PK}-${SK} status to:`, this.bulkStatus);
        }

        // Only update timestamps if provided
        if (this.bulkApprovalTimestamp) {
          const timestamp = parseInt(this.bulkApprovalTimestamp);
          if (!isNaN(timestamp)) {
            updatedDrawing.ApprovalTimestamp = timestamp;
            console.log(`Updating ${PK}-${SK} approval timestamp to:`, timestamp);
          }
        }

        if (this.bulkDenialTimestamp) {
          const timestamp = parseInt(this.bulkDenialTimestamp);
          if (!isNaN(timestamp)) {
            updatedDrawing.DenialTimestamp = timestamp;
            console.log(`Updating ${PK}-${SK} denial timestamp to:`, timestamp);
          }
        }

        if (this.bulkClearedTimestamp) {
          const timestamp = parseInt(this.bulkClearedTimestamp);
          if (!isNaN(timestamp)) {
            updatedDrawing.ClearedTimestamp = timestamp;
            console.log(`Updating ${PK}-${SK} cleared timestamp to:`, timestamp);
          }
        }

        console.log('Sending update for:', PK, SK, updatedDrawing);

        const promise = this.apiService.updateDrawing(updatedDrawing).toPromise().then(response => {
          console.log(`Update response for ${PK}-${SK}:`, 'success');
          return response;
        });

        promises.push(promise);
      } else {
        console.warn('Drawing object is falsy for key:', key);
      }
    }

    try {
      console.log('Executing', promises.length, 'API calls');
      const responses = await Promise.all(promises);
      const successCount = responses.filter(r => r.ok).length;
      
      console.log(`API responses: ${successCount}/${promises.length} successful`);
      
      if (successCount === promises.length) {
        // Update local data
        selectedKeys.forEach(key => {
          const pipeIndex = key.indexOf('|');
          if (pipeIndex === -1) return;
          
          const PK = key.substring(0, pipeIndex);
          const skString = key.substring(pipeIndex + 1);
          const SK = parseInt(skString); // Convert back to number
          
          const index = this.allDrawings.findIndex(d => d.PK === PK && d.SK === SK);
          if (index !== -1) {
            console.log(`Updating local data for ${PK}-${SK}`);
            // Only update fields that were provided
            if (this.bulkStatus !== '') {
              this.allDrawings[index].ApprovalStatus = this.bulkStatus;
            }

            if (this.bulkApprovalTimestamp) {
              const timestamp = parseInt(this.bulkApprovalTimestamp);
              if (!isNaN(timestamp)) {
                this.allDrawings[index].ApprovalTimestamp = timestamp;
              }
            }

            if (this.bulkDenialTimestamp) {
              const timestamp = parseInt(this.bulkDenialTimestamp);
              if (!isNaN(timestamp)) {
                this.allDrawings[index].DenialTimestamp = timestamp;
              }
            }

            if (this.bulkClearedTimestamp) {
              const timestamp = parseInt(this.bulkClearedTimestamp);
              if (!isNaN(timestamp)) {
                this.allDrawings[index].ClearedTimestamp = timestamp;
              }
            }
          }
        });
        
        this.applyFilters();
        this.selectedDrawings.clear();
        this.showBulkConfirm = false;
        this.bulkStatus = '';
        this.bulkApprovalTimestamp = '';
        this.bulkDenialTimestamp = '';
        this.bulkClearedTimestamp = '';
        console.log(`Successfully updated ${successCount} drawings`);
        alert(`Successfully updated ${successCount} drawings!`);
      } else {
        console.error(`Only ${successCount} of ${promises.length} updates succeeded`);
        alert(`Warning: Only ${successCount} of ${promises.length} updates succeeded. Check console for details.`);
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`Error during bulk update: ${errorMessage}`);
    }
    
    this.loading = false;
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'approved': return '#28a745';
      case 'denied': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  }
}
